import { useCallback, useEffect, useState } from 'react';

import { DividerPosition } from '../../ResizeDivider';

export type UseResizePanelProps = {
  open?: boolean;
  width?: number;
  openRight?: boolean;
  onResize?: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  resizeAffordance?: number;
  closedWidth?: number;
};

export default ({
  width,
  minWidth,
  maxWidth,
  openRight,
  onResize,
  open,
  resizeAffordance = 16,
  closedWidth,
}: UseResizePanelProps) => {
  const [initialDragX, setInitialDragX] = useState(0);
  const [widthBeforeDrag, setWidthBeforeDrag] = useState<number | undefined>(
    width,
  );
  const [panelWidth, setPanelWidth] = useState<number | undefined>(width);
  const [isResizing, setIsResizing] = useState(false);
  const [maxReached, setMaxReached] = useState(false);
  const [minReached, setMinReached] = useState(false);

  useEffect(() => {
    setPanelWidth(width);
    setWidthBeforeDrag(width);
  }, [width]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isResizing && widthBeforeDrag && width) {
        e.persist();
        const deltaX = openRight
          ? e.pageX - initialDragX
          : initialDragX - e.pageX;
        const dragAmount = deltaX * window.devicePixelRatio;
        let newWidth = widthBeforeDrag + dragAmount / window.devicePixelRatio;

        if (minWidth && newWidth < minWidth) {
          newWidth = minWidth;
          setMinReached(true);
        } else if (maxWidth && newWidth > maxWidth) {
          newWidth = maxWidth;
          setMaxReached(true);
        } else {
          setMinReached(false);
          setMaxReached(false);
        }

        setPanelWidth(newWidth);

        if (onResize) {
          onResize(newWidth);
        }
      }
    },
    [
      initialDragX,
      isResizing,
      maxWidth,
      minWidth,
      onResize,
      openRight,
      width,
      widthBeforeDrag,
    ],
  );

  const startResizing = useCallback(
    (e: React.MouseEvent) => {
      if (!isResizing) {
        e.persist();
        setIsResizing(true);
        setInitialDragX(e.pageX);
      }
    },
    [isResizing],
  );

  const stopResizing = useCallback(
    (e: React.MouseEvent) => {
      if (isResizing) {
        e.persist();
        setIsResizing(false);
        setWidthBeforeDrag(panelWidth);
        setMinReached(false);
        setMaxReached(false);
      }
    },
    [isResizing, panelWidth],
  );

  useEffect(() => {
    if (onResize) {
      if (open && panelWidth) {
        onResize(panelWidth);
      } else {
        onResize(0);
      }
    }
  }, [panelWidth, onResize, open]);

  const getResizeDividerProps = useCallback(
    () => ({
      onMouseDown: startResizing,
      onMouseUp: stopResizing,
      onMouseMove: handleMouseMove,
      position: openRight ? DividerPosition.right : DividerPosition.left,
      vertical: true,
      affordanceWidth: resizeAffordance,
      minReached,
      maxReached,
    }),
    [
      handleMouseMove,
      maxReached,
      minReached,
      openRight,
      resizeAffordance,
      startResizing,
      stopResizing,
    ],
  );

  const getPanelProps = useCallback(
    <T>(props: T) => ({
      minWidth: closedWidth,
      open,
      width: panelWidth,
      disableAnimation: isResizing,
      ...props,
    }),
    [closedWidth, isResizing, open, panelWidth],
  );

  return {
    getResizeDividerProps,
    getPanelProps,
  };
};
